import React from 'react';
import { Checkbox } from '@mui/material';
import { commentModels, Images, KeyPurpose, PermissionList, RolePermission } from 'utils';
import { CommentsPopper, DateRow, DateRowStartEnd, LinkRow, PriceRow, SimpleTooltip, TextRow, ToolInfo, TypeRow } from 'components';
import { ClaimPaymentActions } from './core/claimPaymentActions';
import { OpenClaimDetails } from '../claim/openClaimDetails';
import { EraValidationFlags } from './core/eraValidationFlags';
import { clearHouseDisplayNames, clearHouseTypeEnums } from '../../../fragments/system/core/constants';

export const paymentMethodEnums = {
    ACH: 'ACH',
    CHECK: 'CHECK',
    WIRE: 'WIRE',
    CASH: 'CASH',
};
export const paymentMethods = [
    { id: paymentMethodEnums.ACH, name: 'ACH' },
    { id: paymentMethodEnums.CHECK, name: 'Check' },
    { id: paymentMethodEnums.WIRE, name: 'Wire' },
    { id: paymentMethodEnums.CASH, name: 'Cash' },
];

export const remittanceStatusEnums = {
    OPEN: 'OPEN',
    APPLIED: 'APPLIED',
    VOID: 'VOID',
};

export const claimPaymentStatus = [
    { id: remittanceStatusEnums.OPEN, name: 'Open' },
    { id: remittanceStatusEnums.APPLIED, name: 'Applied' },
    { id: remittanceStatusEnums.VOID, name: 'Void' },
];

const label = { inputProps: { 'aria-label': 'Color switch demo' } };

export const claimPaymentTableInfo = (handleAction, fullList, selected, handleSelect, afterSuccess, activeRow) => {
    return {
        loadingType: 'GET_REMITTANCE',
        head: [
            {
                renderView: (
                    <SimpleTooltip title={<p>Select All</p>} placement="top-start">
                        <Checkbox
                            {...label}
                            onClick={(e) => handleSelect(e, '', 'all')}
                            name={'checkBox'}
                            style={{ padding: 0, color: '#347AF0' }}
                            checked={fullList?.length === selected?.length}
                        />
                    </SimpleTooltip>
                ),
                width: '35px',
            },
            { name: 'displayId', searchKey: 'DISPLAY_ID', title: 'Display ID', width: '10%' },
            { name: 'payerName', title: 'Payer', custom: false, width: '5%' },
            { name: 'source', title: 'Source', custom: false, width: '7%' },
            { name: 'createdAt', title: 'Created At', disabled: true, iconType: 'date', width: '7%' },
            { name: 'paymentDate', title: 'Pmt Date', iconType: 'date', disabled: true, width: '7%' },
            { name: 'dateRange', title: 'DOS', custom: false, iconType: 'date', disabled: true, width: '7%' },
            { name: 'bankName', title: 'Bank', custom: false, width: '6%' },
            { name: 'paymentReference', searchKey: 'REFERENCE', title: 'Ref #', custom: false, width: '10%' },
            { name: 'totalAmt', searchKey: 'TOTAL_AMT', title: 'Total Amt', iconType: 'date', width: '8%' },
            { name: 'appliedAmt', title: 'Applied Amt', disabled: true, iconType: 'date', width: '8%' },
            { name: 'adjustmentAmount', title: 'Adj. Amt', custom: false, width: '8%' },
            { name: 'openAmt', title: 'Open Amt', disabled: true, iconType: 'date', width: '8%' },
            {
                name: 'statuses',
                title: 'Status',
                disabled: true,
                width: '7%',
                multiple: true,
                filterList: [{ name: 'All', id: 'All' }, ...claimPaymentStatus],
                defaultSelected: [remittanceStatusEnums.OPEN, remittanceStatusEnums.APPLIED],
            },
            { name: '', title: 'Action', custom: false, width: '8%' },
        ],

        body: (item) => {
            return [
                {
                    value: (
                        <Checkbox
                            {...label}
                            onClick={(e) => handleSelect(e, item)}
                            name={'checkBox'}
                            style={{
                                padding: 0,
                                color: '#347AF0',
                                marginLeft: '-3px',
                                marginRight: '-1px',
                            }}
                            checked={selected?.includes(item?.id)}
                        />
                    ),
                    notClickable: true,
                    width: '35px',
                },
                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 8 }}>
                            <LinkRow
                                name={item?.displayId ? item.displayId : '--'}
                                href={RolePermission([PermissionList.CLAIM_PMT_READ?.code]) ? `/claim-payment/${item?.id}` : ''}
                                checkPermission={item?.id ? RolePermission([PermissionList.CLAIM_PMT_READ?.code]) : null}
                            />
                            <EraValidationFlags
                                item={item}
                                paymentId={item?.id}
                                displayId={item?.displayId}
                                afterSuccess={() => afterSuccess('noLoad')}
                            />
                            <CommentsPopper
                                afterSuccess={() => afterSuccess('noLoad')}
                                buttonClassName="comments-trigger-inline"
                                count={item?.commentCount}
                                title={`Claim Payment ${item?.displayId || ''}`}
                                attachmentPurpose={KeyPurpose.CLAIM_DOC}
                                enableAiImprove
                                resources={[
                                    { onModel: commentModels.Remittance, resourceId: item?.id, label: 'Claim Payment' },
                                    ...(item?.claims || []).map((claim) => ({
                                        onModel: commentModels.Remittance,
                                        resourceId: claim?.id,
                                        label: claim?.displayId,
                                    })),
                                ]}
                            />
                        </div>
                    ),
                    notClickable: true,
                    width: '10%',
                },
                {
                    value: item?.payerShortName ? (
                        <SimpleTooltip title={item?.payerName} placement="top-end">
                            <div className="full-width">
                                <LinkRow
                                    name={item?.payerShortName || '--'}
                                    href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.payerId}` : ''}
                                    checkPermission={item?.payerId ? RolePermission([PermissionList.FS_READ.code]) : null}
                                />
                            </div>
                        </SimpleTooltip>
                    ) : (
                        <div className="full-width">
                            <LinkRow
                                name={item?.payerName || '--'}
                                href={RolePermission([PermissionList.FS_READ.code]) ? `/fundingSource/${item?.payerId}` : ''}
                                checkPermission={item?.payerId ? RolePermission([PermissionList.FS_READ.code]) : null}
                            />
                        </div>
                    ),
                    notClickable: true,
                    width: '5%',
                },
                {
                    // Automated payments also say which clearinghouse delivered the ERA.
                    value: (
                        <div>
                            <p>
                                <TypeRow text={item?.source} />
                            </p>
                            {item?.clearinghouse && (
                                <p className="remittance-source-clearinghouse">
                                    {clearHouseDisplayNames[item.clearinghouse] || item.clearinghouse}
                                </p>
                            )}
                        </div>
                    ),
                    notClickable: true,
                    width: '7%',
                },
                {
                    value: <DateRow date={item?.createdAt} />,
                    notClickable: true,
                    width: '7%',
                },
                {
                    value: <DateRow date={item?.paymentDate} />,
                    notClickable: true,
                    width: '7%',
                },
                {
                    value: <DateRowStartEnd start={item?.dos?.early} end={item?.dos?.latest} secondLine={true} />,
                    notClickable: true,
                    width: '7%',
                },
                {
                    value: <TextRow name={item?.bankName} />,
                    notClickable: true,
                    width: '6%',
                },
                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 4 }}>
                            {item?.verified && (
                                <SimpleTooltip title={'This payment has been verified with the bank.'} placement="top-end">
                                    <img src={Images.verifiedBadge} alt="verifiedBadge" />
                                </SimpleTooltip>
                            )}
                            <TextRow name={item?.paymentReference} />
                        </div>
                    ),
                    notClickable: true,
                    width: '10%',
                },
                {
                    value: <PriceRow info={item?.totalAmount} />,
                    notClickable: true,
                    width: '8%',
                },
                {
                    value: <PriceRow info={item?.appliedAmount} />,
                    notClickable: true,
                    width: '8%',
                },

                {
                    value: <PriceRow info={item?.adjustmentAmount} />,
                    notClickable: true,
                    width: '8%',
                },

                {
                    value: <PriceRow info={item?.openAmount} />,
                    notClickable: true,
                    width: '8%',
                },
                {
                    value: renderStatusBadge(item?.status, item?.voidReason),
                    notClickable: true,
                    width: '7%',
                },
                {
                    value: (
                        <div className="flex-align-center" style={{ gap: 16 }}>
                            {/*<button onClick={() => handleAction('down', item)} className="reset-btn-by-default">*/}
                            {/*    <img*/}
                            {/*        style={activeRow === item?.id ? { transform: 'rotate(-180deg)' } : {}}*/}
                            {/*        src={Images.dropDown}*/}
                            {/*        alt="dropDown"*/}
                            {/*    />*/}
                            {/*</button>*/}
                            <ClaimPaymentActions item={item} handleAction={() => handleAction('edit', item)} afterSuccess={() => afterSuccess('noLoad')} />
                        </div>
                    ),
                    notClickable: true,
                    width: '8%',
                },
            ];
        },

        renderAccordionRow: (i) => i?.claims,

        accordionHead: [
            { title: 'Claim ID', width: '15%' },
            { title: 'DOS', width: '12%' },
            { title: 'Client', width: '15%' },
            { title: 'Total Billed', width: '12%' },
            { title: 'Total Allowed ', width: '12%' },
            { title: 'Total Paid ', width: '12%' },
            { title: 'Balance.', width: '12%' },
            { title: 'Aging', width: '10%' },
        ],

        accordionBody: (i, j, parent) => {
            return [
                {
                    value: (
                        <OpenClaimDetails claimId={i?.id} displayId={i?.displayId} afterSuccess={() => afterSuccess('noLoad')} />
                        // <LinkRow
                        //     name={i?.displayId ? i.displayId : '--'}
                        //     href={RolePermission([PermissionList.CLAIM_PMT_READ?.code]) ? `/billedClaim/${i?.id}` : ''}
                        //     checkPermission={i?.id ? RolePermission([PermissionList.CLAIM_PMT_READ?.code]) : null}
                        // />
                    ),
                    width: '15%',
                },
                { value: <DateRowStartEnd start={i?.dos?.early} end={i?.dos?.latest} />, width: '12%' },
                {
                    value: (
                        <LinkRow
                            name={i?.clientFullName ? i.clientFullName : '--'}
                            href={RolePermission([PermissionList.CLIENT_READ?.code]) ? `/client/${i?.clientId}` : ''}
                            checkPermission={i?.clientId ? RolePermission([PermissionList.CLIENT_READ?.code]) : null}
                        />
                    ),
                    width: '15%',
                },
                { value: <PriceRow info={i?.billedAmount} />, width: '12%' },
                { value: <PriceRow info={i?.allowedAmount} />, width: '12%' },
                { value: <PriceRow info={i?.paidAmount} />, width: '12%' },
                { value: <PriceRow info={i?.remaining} />, width: '12%' },
                { value: <div>{i?.aging}</div>, width: '10%' },
            ];
        },

        noDetailsText: 'No claims yet',
        notYet: {
            title: 'No Claim Payments Yet',
            subTitle: 'There are no claim payments yet.',
            image: Images.noPendingClaim,
            height: '250px',
        },
    };
};

export const renderStatusBadge = (status, voidReason) => {
    switch (status) {
        case remittanceStatusEnums.OPEN:
            return (
                <div className="claim-payment-remittance-box" style={{ color: '#B54708', background: '#FFFAEB' }}>
                    Open
                </div>
            );
        case remittanceStatusEnums.APPLIED:
            return (
                <div className="claim-payment-remittance-box" style={{ color: '#027A48', background: '#ECFDF3' }}>
                    Applied
                </div>
            );
        case remittanceStatusEnums.VOID:
            return (
                <div className="flex-align-center" style={{ gap: 4 }}>
                    <div className="claim-payment-remittance-box" style={{ color: '#B42318', background: '#FEF3F2' }}>
                        Voided
                    </div>
                    {voidReason && (
                        <div style={{ marginTop: '3px' }}>
                            <ToolInfo text={voidReason} />
                        </div>
                    )}
                </div>
            );
        default:
            return null;
    }
};

export const secondaryStatusEnums = {
    MARKED: 'MARKED',
    READY: 'READY',
    SUBMITTED: 'SUBMITTED',
};

/**
 * Sec. Billing filter options. No 'All' entry — that is a dropdown affordance for clearing
 * the filter, so the selector prepends it (same shape as the payer list). Kept here so the
 * ClaimFilters dropdown and the Claims active-filter chip resolve the same label.
 */
export const secondaryBillingList = [
    { id: secondaryStatusEnums.MARKED, name: 'Ready for Secondary', img: Images.secondaryBillingFilled },
    { id: secondaryStatusEnums.READY, name: 'Secondary Claim On', img: Images.secondaryReady },
    { id: secondaryStatusEnums.SUBMITTED, name: 'Submitted to Secondary', img: Images.secondarySubmitted },
];

export const renderSecondaryBillingImage = (item) => {
    if (item?.secondaryBillingState === secondaryStatusEnums.READY && item?.submitted) {
        return Images.secondaryReadySubmitted;
    }
    if (item?.submitted) {
        return Images.secondarySubmitted;
    }

    switch (item?.secondaryBillingState) {
        case secondaryStatusEnums.MARKED:
            return Images.secondaryBillingFilled;

        case secondaryStatusEnums.READY:
            return Images.secondaryReady;

        default:
            return Images.secondaryBilling;
    }
};

export const renderSecondaryBillingInfoText = (item) => {
    if (item?.secondaryBillingState === secondaryStatusEnums.READY && item?.submitted) {
        return 'Submitted to Secondary and Secondary Claim is On';
    }
    if (item?.submitted) {
        return 'Submitted to Secondary';
    }

    switch (item?.secondaryBillingState) {
        case secondaryStatusEnums.MARKED:
            return 'Ready for Secondary';

        case secondaryStatusEnums.READY:
            return 'Secondary Claim On';

        default:
            return ' ';
    }
};

// Remittance origin. `ERA` payments are ingested automatically from the clearinghouse
// (835 files / Stedi webhooks); `MANUAL` payments are entered by staff. Mirrors the
// backend `RemittanceSource` enum (package/src/remittance/remittance.constants.ts).
export const remittanceSourceEnums = {
    ERA: 'ERA',
    MANUAL: 'MANUAL',
    AUTOMATION: 'AUTOMATION',
};
// Clearinghouse filter — which clearinghouse delivered an automated payment's ERA.
export const remittanceClearinghouseList = [
    { id: 'All', name: 'All' },
    ...[clearHouseTypeEnums.TL_CLEARINGHOUSE, clearHouseTypeEnums.OFFICE_ALLY, clearHouseTypeEnums.AVAILITY].map((id) => ({
        id,
        name: clearHouseDisplayNames[id],
    })),
];
export const remittanceSourceList = [
    { id: 'All', name: 'All' },
    { id: remittanceSourceEnums.ERA, name: 'Automation' },
    { id: remittanceSourceEnums.MANUAL, name: 'Manual' },
];
